export type PlaceLookupAddressComponent = {
  longText: string | null;
  shortText: string | null;
  long_name: string | null;
  short_name: string | null;
  types: string[];
};

export type PlaceLookupPlace = {
  placeId: string | null;
  name: string | null;
  displayName: string | null;
  formattedAddress: string | null;
  types: string[];
  addressComponents: PlaceLookupAddressComponent[];
};

type PlaceLookupResponse = {
  places?: unknown[];
  meta?: {
    geocodeStatus?: string | null;
    nearbyStatus?: string | null;
    plusCode?: string | null;
    count?: number | null;
  };
  error?: string;
  details?: string;
};

const REQUEST_TIMEOUT_MS = 12000;

/* ================================
   HELPERS
================================ */

function cleanText(value?: unknown): string | null {
  if (typeof value !== "string") return null;

  const cleaned = value
    .replace(/\s+/g, " ")
    .trim();

  return cleaned.length > 0 ? cleaned : null;
}

function normalizeKey(value?: unknown): string {
  return (
    cleanText(value)
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/gi, " ")
      .trim() ?? ""
  );
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of value) {
    const cleaned = cleanText(item);
    if (!cleaned) continue;

    const key = normalizeKey(cleaned);
    if (!key || seen.has(key)) continue;

    seen.add(key);
    result.push(cleaned);
  }

  return result;
}

function pickFirstText(...values: (unknown | null | undefined)[]): string | null {
  for (const value of values) {
    const cleaned = cleanText(value);
    if (cleaned) return cleaned;
  }
  return null;
}

function looksLikePlusCode(value?: unknown): boolean {
  const text = cleanText(value)?.toUpperCase();
  if (!text) return false;

  return /^[A-Z0-9]{2,8}\+[A-Z0-9]{2,8}$/.test(text);
}

function normalizeAddressComponent(value: unknown): PlaceLookupAddressComponent | null {
  if (!value || typeof value !== "object") return null;

  const item = value as Record<string, unknown>;

  const normalized: PlaceLookupAddressComponent = {
    longText: pickFirstText(item.longText, item.long_name, item.name),
    shortText: pickFirstText(item.shortText, item.short_name),
    long_name: pickFirstText(item.long_name, item.longText, item.name),
    short_name: pickFirstText(item.short_name, item.shortText),
    types: normalizeStringArray(item.types),
  };

  const hasUsefulText =
    !!normalized.longText ||
    !!normalized.shortText ||
    !!normalized.long_name ||
    !!normalized.short_name;

  if (!hasUsefulText && normalized.types.length === 0) {
    return null;
  }

  return normalized;
}

function getDisplayName(value: unknown): string | null {
  if (!value) return null;

  if (typeof value === "string") {
    return cleanText(value);
  }

  if (typeof value === "object") {
    const item = value as Record<string, unknown>;

    return pickFirstText(
      item.text,
      item.name,
      item.longText,
      item.long_name
    );
  }

  return null;
}

function normalizePlace(value: unknown): PlaceLookupPlace | null {
  if (!value || typeof value !== "object") return null;

  const item = value as Record<string, unknown>;

  const addressComponents = Array.isArray(item.addressComponents)
    ? item.addressComponents
        .map(normalizeAddressComponent)
        .filter(
          (component): component is PlaceLookupAddressComponent => component !== null
        )
    : [];

  const placeId = pickFirstText(item.placeId, item.id);
  const name = pickFirstText(item.name);
  const displayName = pickFirstText(
    getDisplayName(item.displayName),
    getDisplayName(item.display_name),
    name
  );
  const formattedAddress = pickFirstText(
    item.formattedAddress,
    item.formatted_address,
    item.vicinity
  );

  const types = normalizeStringArray(item.types);

  const hasUsefulContent =
    !!placeId ||
    !!name ||
    !!displayName ||
    !!formattedAddress ||
    addressComponents.length > 0 ||
    types.length > 0;

  if (!hasUsefulContent) {
    return null;
  }

  return {
    placeId,
    name,
    displayName,
    formattedAddress,
    types,
    addressComponents,
  };
}

function buildPlaceDedupKey(place: PlaceLookupPlace): string {
  const idKey = normalizeKey(place.placeId);
  if (idKey) return `id:${idKey}`;

  const nameKey = normalizeKey(place.displayName || place.name);
  const addressKey = normalizeKey(place.formattedAddress);

  if (nameKey && addressKey) return `name_addr:${nameKey}|${addressKey}`;
  if (nameKey) return `name:${nameKey}`;
  if (addressKey) return `addr:${addressKey}`;

  return `fallback:${JSON.stringify({
    types: place.types,
    addressComponents: place.addressComponents.length,
  })}`;
}

function scorePlace(place: PlaceLookupPlace): number {
  let score = 0;

  if (place.placeId) score += 30;
  if (place.displayName && !looksLikePlusCode(place.displayName)) score += 25;
  if (place.name && !looksLikePlusCode(place.name)) score += 20;
  if (place.formattedAddress) score += 10;
  if (place.addressComponents.length > 0) score += 20;
  if (place.types.length > 0) score += 10;

  const typeSet = new Set(place.types);

  if (typeSet.has("premise") || typeSet.has("subpremise")) score += 20;
  if (typeSet.has("point_of_interest")) score += 15;
  if (typeSet.has("establishment")) score += 10;
  if (typeSet.has("store")) score += 10;
  if (typeSet.has("route")) score -= 10;

  if (looksLikePlusCode(place.displayName) || looksLikePlusCode(place.name)) {
    score -= 40;
  }

  return score;
}

function dedupeAndSortPlaces(places: PlaceLookupPlace[]): PlaceLookupPlace[] {
  const bestByKey = new Map<string, { place: PlaceLookupPlace; score: number }>();

  for (const place of places) {
    const key = buildPlaceDedupKey(place);
    const score = scorePlace(place);
    const existing = bestByKey.get(key);

    if (!existing || score > existing.score) {
      bestByKey.set(key, { place, score });
    }
  }

  return Array.from(bestByKey.values())
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.place);
}

async function readJsonResponse(
  response: Response
): Promise<PlaceLookupResponse | null> {
  const rawText = await response.text();

  try {
    return rawText ? (JSON.parse(rawText) as PlaceLookupResponse) : null;
  } catch {
    console.warn("Place lookup returned a non-JSON response.");
    return null;
  }
}

/* ================================
   MAIN LOOKUP
================================ */

export async function lookupNearbyPlaces(
  lat: number,
  lng: number
): Promise<PlaceLookupPlace[]> {
  const endpoint = cleanText(process.env.EXPO_PUBLIC_PLACE_LOOKUP_URL);

  if (!endpoint) {
    console.warn("Missing EXPO_PUBLIC_PLACE_LOOKUP_URL");
    return [];
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    console.warn("Place lookup received invalid coordinates.");
    return [];
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ lat, lng }),
      signal: controller.signal,
    });

    const data = await readJsonResponse(response);

    if (!response.ok) {
      console.warn("Place lookup request failed.", { status: response.status });
      return [];
    }

    const rawPlaces = Array.isArray(data?.places) ? data.places : [];

    const normalizedPlaces = rawPlaces
      .map(normalizePlace)
      .filter((place): place is PlaceLookupPlace => place !== null);

    const finalPlaces = dedupeAndSortPlaces(normalizedPlaces);

    if (__DEV__) {
      console.log("Place lookup completed.", {
        resultCount: finalPlaces.length,
      });
    }

    return finalPlaces;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      console.warn("lookupNearbyPlaces timed out");
      return [];
    }

    console.warn("Place lookup failed.");
    return [];
  } finally {
    clearTimeout(timeout);
  }
}
